import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ProductService } from '@/services/product.services';
import type {
  IApiResponseProduct,
  IPoductBody,
  IProductImage,
  IProductRecommendationRemoveBody,
  IProductRelationBody,
  IUpdateProductBody,
  IBodySetVisibility,
  IBodyAddAddonGroup,
  IBodyUpdateAddonGroup,
  IBodyAddAddonOption,
  IAddonGroupsResponse,
} from '@/types/product.types';
import {
  form_product_step_one,
  form_product_step_oneIdImage,
} from '@/refs/product.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';
import { isGlobalError } from '@/refs/common.refs';

/**
 * Store pour gérer les produits et leurs actions associées
 */
export const useProductStore = defineStore('productStore', () => {
  /**
   * État réactif contenant la liste des produits récupérés depuis l’API.
   *
   * @type {Ref<IApiResponseProduct | null>}
   */
  const datagetProduct = ref<IApiResponseProduct | null>(null);

  /**
   * Crée un nouveau produit dans le système.
   *
   * @param {IProductBody} data - Données nécessaires pour la création du produit.
   * @returns {Promise<any>} - Résultat de la requête envoyée à l'API.
   */
  const createProduct = async (data: IPoductBody): Promise<any> => {
    try {
      const response = await ProductService.createProduct(data);
      form_product_step_one.image = response?.data?.data?.id;
      return response;
    } catch (error) {
      // On renvoie l'erreur pour permettre une gestion plus haut dans la stack (toast, logs, etc.)
      return Promise.reject(error);
    }
  };

  /**
   * Récupère la liste des produits depuis le service distant.
   * Met à jour l’état réactif `datagetProduct` avec les données obtenues.
   *
   * @async
   * @function getProduct
   * @returns {Promise<IApiResponseProduct>} Les données des produits récupérées depuis l’API.
   */
  const getProduct = async (
    organizationId?: string,
  ): Promise<IApiResponseProduct> => {
    try {
      const response = await ProductService.getProduct(organizationId);
      datagetProduct.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getProductForCollections = async (
    organizationId?: string,
  ): Promise<IApiResponseProduct> => {
    try {
      const response =
        await ProductService.getProductForCollections(organizationId);
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Récupère les informations d’un produit à partir de son identifiant.
   * Met à jour l’état réactif `datagetProduct` avec les données obtenues.
   *
   * @async
   * @function getProductById
   * @param {string} productId - Identifiant du produit à récupérer.
   * @returns {Promise<IApiResponseProduct>} Données du produit récupérées depuis l’API.
   */
  const getProductById = async (
    productId: string,
  ): Promise<IApiResponseProduct> => {
    try {
      const response = await ProductService.getProductById(productId);
      datagetProduct.value = response.data;
      return response.data;
    } catch (error) {
      // Permet de gérer l'erreur au niveau supérieur (toast, alert, logs, etc.)
      return Promise.reject(error);
    }
  };

  /**
   * Met à jour un produit existant.
   *
   * @param {string} productId - Identifiant du produit à mettre à jour.
   * @param {IUpdateProductBody} data - Données à appliquer lors de la mise à jour.
   * @returns {Promise<any>} - Réponse de l'API après mise à jour.
   */
  const updateProduct = async (
    productId: string,
    data: IUpdateProductBody,
  ): Promise<any> => {
    try {
      const response = await ProductService.UpdateProduct(data, productId);

      console.log(response);
      return response;
    } catch (error) {
      console.log(error);
      // On propage l'erreur pour une gestion au niveau supérieur
      return Promise.reject(error);
    }
  };

  /**
   * Supprime un produit à partir de son identifiant.
   *
   * @param {string} productId - Identifiant du produit à supprimer.
   * @returns {Promise<any>} - Réponse de l'API après suppression.
   */
  const deleteProduct = async (productId: string): Promise<any> => {
    try {
      const response = await ProductService.DeleteProduct(productId);
      return response;
    } catch (error) {
      // On renvoie l'erreur pour une gestion externe (toast, modal, logs, etc.)
      return Promise.reject(error);
    }
  };

  /**
   * Upload une image liée à un produit.
   *
   * @param {IProductImage} data - Données de l'image à envoyer au serveur.
   * @returns {Promise<any>} - Réponse de l'API après traitement de l'upload.
   */
  const uploadImage = async (data: IProductImage): Promise<any> => {
    try {
      //   isLoaderLoginFormActivated.value = true;
      const response = await ProductService.uploadImage(data);
      form_product_step_one.image = response?.data?.data?.id;
      form_product_step_oneIdImage.value = response?.data?.data?.id;

      return response;
    } catch (error) {
      // On propage l'erreur pour une gestion au niveau supérieur
      return Promise.reject(error);
    }
  };

  /**
   * Crée une nouvelle recommandation de produit dans le système.
   *
   * @param {IProductRelationBody} data - Données nécessaires pour créer la recommandation.
   * @returns {Promise<any>} - Résultat de la requête API après création de la recommandation.
   */
  const createRecommendation = async (
    data: IProductRelationBody,
  ): Promise<any> => {
    try {
      const response = await ProductService.createRecommendation(data);
      return response;
    } catch (error) {
      // Propagation de l'erreur pour gestion externe (toast, logs, etc.)
      return Promise.reject(error);
    }
  };

  /**
   * Supprime une ou plusieurs recommandations de produit.
   *
   * @param {IProductRecommendationRemoveBody} data - Données contenant les identifiants des produits et des recommandations à supprimer.
   * @returns {Promise<any>} - Résultat de la requête API après suppression.
   */
  const deleteRecommendation = async (
    data: IProductRecommendationRemoveBody,
  ): Promise<any> => {
    try {
      const response = await ProductService.deleteRecommendation(data);
      return response;
    } catch (error) {
      // Propagation de l'erreur pour gestion externe (toast, modal, logs, etc.)
      return Promise.reject(error);
    }
  };

  const getRecommendation = async (): Promise<any> => {
    const response = await ProductService.getRecomandation();
    datagetProduct.value = response.data;
    return response.data;
  };

  // ─────────────────────────────────────────────────────────────
  // VISIBILITY
  // ─────────────────────────────────────────────────────────────

  const setVisibility = async (
    productId: string,
    data: IBodySetVisibility,
  ): Promise<any> => {
    try {
      const response = await ProductService.setVisibility(productId, data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // ADDON GROUPS (extras)
  // ─────────────────────────────────────────────────────────────

  const dataGetAddonGroups = ref<IAddonGroupsResponse | null>(null);

  const addAddonGroup = async (
    productId: string,
    data: IBodyAddAddonGroup,
  ): Promise<any> => {
    try {
      const response = await ProductService.addAddonGroup(productId, data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getAddonGroups = async (
    productId: string,
  ): Promise<IAddonGroupsResponse> => {
    try {
      const response = await ProductService.getAddonGroups(productId);
      dataGetAddonGroups.value = response?.data ?? null;
      return response?.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const updateAddonGroup = async (
    data: IBodyUpdateAddonGroup,
  ): Promise<any> => {
    try {
      const response = await ProductService.updateAddonGroup(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const deleteAddonGroup = async (): Promise<any> => {
    try {
      const response = await ProductService.deleteAddonGroup();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const addAddonOption = async (data: IBodyAddAddonOption): Promise<any> => {
    try {
      const response = await ProductService.addAddonOption(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const updateAddon = async (data: Record<string, any>): Promise<any> => {
    try {
      const response = await ProductService.updateAddon(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const deleteAddon = async (): Promise<any> => {
    try {
      const response = await ProductService.deleteAddon();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  return {
    datagetProduct,
    dataGetAddonGroups,
    getProduct,
    getProductForCollections,
    getProductById,
    getRecommendation,
    createProduct,
    updateProduct,
    deleteProduct,
    uploadImage,
    createRecommendation,
    deleteRecommendation,
    setVisibility,
    addAddonGroup,
    getAddonGroups,
    updateAddonGroup,
    deleteAddonGroup,
    addAddonOption,
    updateAddon,
    deleteAddon,
  };
});
